import { useState } from "react";

import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAuthStore } from "@/store/authStore";

function initials(name?: string | null, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
    }
    if (parts.length === 1 && parts[0]) {
      return parts[0].slice(0, 2).toUpperCase();
    }
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "?";
}

export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, signOut } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!me) return null;

  const activeOrg = me.accessible_orgs.find((o) => o.id === me.active_org_id);

  const handleSignOut = async () => {
    await signOut();
    setAnchorEl(null);
    navigate("/login", { replace: true });
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={t("auth.myAccount")}
        sx={{ p: 0.5 }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {initials(me.full_name, me.email)}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { minWidth: 280 } } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontWeight: 700,
              }}
            >
              {initials(me.full_name, me.email)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {me.full_name ?? me.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {me.email}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: "wrap", gap: 0.5 }}>
            {me.is_god && <Chip size="small" color="secondary" label="GOD" />}
            {activeOrg && (
              <Chip
                size="small"
                variant="outlined"
                label={`${activeOrg.role_code} · ${activeOrg.code}`}
              />
            )}
            {!me.is_god && !activeOrg && me.tenant_id && (
              <Chip size="small" variant="outlined" label="TENANT" />
            )}
          </Stack>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate("/profile");
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("auth.profile")}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t("auth.signOut")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
